import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowRight, Filter } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function GalleryPage() {
  const [garments, setGarments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchGarments();
  }, []);

  const fetchGarments = async () => {
    try {
      const response = await axios.get(`${API}/garments`);
      setGarments(response.data);
    } catch (error) {
      console.error("Error fetching garments:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ["all", ...new Set(garments.map(g => g.category))];
  const filteredGarments = filter === "all" 
    ? garments 
    : garments.filter(g => g.category === filter);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="gallery-page">
      <Navbar />
      
      <main className="pt-32 md:pt-40 pb-24">
        <div className="container-custom">
          {/* Header */}
          <div className="mb-12">
            <p className="caption text-[#FF1493] mb-4">Collection</p>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <h1 className="heading-lg mb-4">Design Gallery</h1>
                <p className="body-lg text-gray-400 max-w-xl">
                  Browse our curated collection of custom garment designs. 
                  Each piece is crafted by talented tailors ready to bring your vision to life.
                </p>
              </div>
              
              {/* Filter */}
              <div className="flex items-center gap-3">
                <Filter className="w-4 h-4 text-gray-500" />
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-40 rounded-none bg-[#141414] border-white/10 text-white" data-testid="category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[#141414] border-white/10">
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat} className="capitalize text-white">
                        {cat === "all" ? "All Categories" : cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 spinner"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-testid="garments-grid">
              {filteredGarments.map((garment, index) => (
                <div 
                  key={garment.garment_id} 
                  className="group animate-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`garment-card-${garment.garment_id}`}
                >
                  <Link to="/studio" className="block">
                    <div className="image-container aspect-[3/4] mb-6">
                      <img
                        src={garment.image_url}
                        alt={garment.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </Link>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="caption text-[#FF1493] mb-2">{garment.category}</p>
                      <h3 className="font-heading text-xl mb-2 text-white">{garment.name}</h3>
                      <p className="text-gray-400 text-sm line-clamp-2">{garment.description}</p>
                    </div>
                  </div>
                  <Link 
                    to="/studio" 
                    className="inline-flex items-center gap-2 mt-4 text-[#FF1493] text-sm font-medium hover:gap-3 transition-all"
                    data-testid={`try-on-link-${garment.garment_id}`}
                  >
                    Try It On <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-24 text-center py-16 bg-[#141414] border border-white/10">
            <h2 className="heading-md mb-4">Ready to Try?</h2>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Upload your photo and see yourself in any of these designs instantly.
            </p>
            <Link to="/studio" data-testid="gallery-cta">
              <button className="btn-primary">
                Open Studio
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
